import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams, useRouteContext, useSearch } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { EmptyState, ErrorState, Tabs } from '@bb/ui';
import { ApiError, type CampaignAnalyticsQuery, type CampaignView } from '../../../api/client.ts';
import { PeriodPicker } from '../../analytics/PeriodPicker.tsx';
import { lastDays, periodProblem, type Period } from '../../analytics/period.ts';
import { PushPerformancePanel } from '../../analytics/push/PushPerformancePanel.tsx';
import { ActivityLog } from '../../analytics/activity/ActivityLog.tsx';
import { FunnelReportPanel } from '../../analytics/reports/FunnelReportPanel.tsx';
import { RetentionReportPanel } from '../../analytics/reports/RetentionReportPanel.tsx';
import { VariantStatsPanel } from '../../analytics/variants/VariantStatsPanel.tsx';
import { ChangesBadge } from '../../history/ChangesBadge.tsx';
import { CampaignStatusBadge } from '../CampaignStatusBadge.tsx';
import { BREAKDOWN_LABELS, campaignAnalyticsSearch, formatDateTime, hourlyAllowed, HOURLY_MAX_DAYS, TAB_LABELS, type AnalyticsTab, type CampaignAnalyticsSearch } from './analytics.ts';
import { BreakdownTable } from './BreakdownTable.tsx';
import { ChannelTable } from './ChannelTable.tsx';
import { KpiTiles, type EstimatedAudience } from './KpiTiles.tsx';
import { PerformanceChart } from './PerformanceChart.tsx';
import { ApiIdsPanel, ConversionsPanel, SettingsSummary } from './SettingsSummary.tsx';

const ROUTE = '/env/$environmentId/campaigns/$campaignId/analytics';
const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

function useAnalyticsSearch() {
  const search = useSearch({ from: ROUTE });
  const navigate = useNavigate({ from: ROUTE });
  const period: Period = search.from && search.to ? { from: search.from, to: search.to } : lastDays(30);
  // Filters change the URL of the same page: the page stays where it is scrolled.
  const set = (patch: Partial<CampaignAnalyticsSearch>) => void navigate({ search: (prev: CampaignAnalyticsSearch) => campaignAnalyticsSearch({ ...prev, ...patch }), replace: true, resetScroll: false });
  return { search, period, set };
}

/** The estimated audience of the published revision (BB-44); a campaign without an audience filter targets everyone. */
function useEstimate(environmentId: string, campaign: CampaignView): EstimatedAudience {
  const { api } = useRouteContext({ from: ROUTE });
  const audience = campaign.published?.content.audience ?? null;
  const estimate = useQuery({ queryKey: ['campaign-analytics', environmentId, campaign.id, 'estimate', campaign.published?.revision], queryFn: () => api.estimateAudience(environmentId, audience), enabled: audience !== null, staleTime: 60_000 });
  if (audience === null) return { state: 'everyone' };
  if (estimate.isError) return { state: 'error', message: errorText(estimate.error) };
  return estimate.data ? { state: 'ready', reach: estimate.data } : { state: 'loading' };
}

/**
 * Overview of the campaign analytics (BB-104): KPIs with their formulas, performance over time, a breakdown, Message
 * Performance per channel (push through the push panel of BB-79), conversions and control, the published settings
 * and API ids — for a period, granularity and breakdown kept in the URL.
 */
function Overview({ environmentId, campaign }: { environmentId: string; campaign: CampaignView }) {
  const { api } = useRouteContext({ from: ROUTE });
  const { search, period, set } = useAnalyticsSearch();
  const valid = periodProblem(period) === null;
  const hourly = hourlyAllowed(period);
  const granularity = search.granularity === 'hour' && hourly ? 'hour' : 'day';
  const breakdown = search.breakdown ?? 'channel';
  const query: CampaignAnalyticsQuery = { from: period.from, to: period.to, granularity, breakdown };
  const analytics = useQuery({ queryKey: ['campaign-analytics', environmentId, campaign.id, query], queryFn: () => api.campaignAnalytics(environmentId, campaign.id, query), enabled: valid, placeholderData: keepPreviousData });
  const estimate = useEstimate(environmentId, campaign);
  const data = analytics.data;
  const variantLabels = data ? Object.fromEntries([...data.apiIds.variants.map((v) => [v.apiId, v.name]), ...(data.apiIds.control ? [[data.apiIds.control, 'Control']] : [])]) : undefined;
  return (
    <>
      <div className="filters" role="group" aria-label="Filters">
        <PeriodPicker value={period} onChange={(p) => set({ from: p.from, to: p.to })} />
        <label title={hourly ? undefined : `Hourly numbers cover at most ${HOURLY_MAX_DAYS} days.`}>
          Granularity{' '}
          <select value={granularity} onChange={(e) => set({ granularity: e.target.value === 'hour' ? 'hour' : undefined })}>
            <option value="day">Day</option>
            <option value="hour" disabled={!hourly}>
              Hour
            </option>
          </select>
        </label>
        <label>
          Breakdown{' '}
          <select value={breakdown} onChange={(e) => set({ breakdown: e.target.value === 'channel' ? undefined : (e.target.value as CampaignAnalyticsSearch['breakdown']) })}>
            {Object.entries(BREAKDOWN_LABELS).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
        </label>
      </div>
      {search.granularity === 'hour' && !hourly && <p className="hint">Hourly numbers cover at most {HOURLY_MAX_DAYS} days: showing days for this period.</p>}
      {analytics.isError ? (
        <ErrorState title="Analytics could not be loaded" message={errorText(analytics.error)} {...(analytics.error instanceof ApiError ? { code: analytics.error.code } : {})} onRetry={() => void analytics.refetch()} />
      ) : !data ? (
        valid && <p aria-busy="true">Loading analytics…</p>
      ) : (
        <>
          <p className="hint" aria-label="Data freshness">
            {data.from} – {data.to}, {data.timezone}. Live numbers computed {formatDateTime(data.dataWatermark.generatedAt)};{' '}
            {data.dataWatermark.lastFactRecordedAt ? `newest event included ${formatDateTime(data.dataWatermark.lastFactRecordedAt)}` : 'no events in this period yet'}. Test sends and the control group are left out.
          </p>
          <KpiTiles kpis={data.kpis} estimate={estimate} />
          <h2>Performance over time</h2>
          <PerformanceChart data={data} />
          <h2>Breakdown by {BREAKDOWN_LABELS[data.breakdown].toLowerCase()}</h2>
          <BreakdownTable data={data} />
          <h2>Message performance</h2>
          {campaign.channels.includes('push') && <PushPerformancePanel environmentId={environmentId} scope={{ campaignId: campaign.id }} period={period} heading="h3" {...(variantLabels ? { variantLabels } : {})} />}
          <ChannelTable data={data} />
          <ConversionsPanel data={data} />
          <SettingsSummary campaign={campaign} data={data} />
          <ApiIdsPanel data={data} />
        </>
      )}
    </>
  );
}

/**
 * Campaign → Analytics (BB-104): the overview, and tabs for variant statistics, funnel, retention and the Message
 * Activity Log; the tab, period and options live in the URL.
 */
export function CampaignAnalyticsPage() {
  const { environmentId, campaignId } = useParams({ from: ROUTE });
  const { api } = useRouteContext({ from: ROUTE });
  const { search, period, set } = useAnalyticsSearch();
  const campaign = useQuery({ queryKey: ['campaigns', environmentId, 'one', campaignId], queryFn: () => api.getCampaign(environmentId, campaignId) });
  if (campaign.isPending) return <p aria-busy="true">Loading campaign…</p>;
  if (campaign.isError) return <ErrorState title="Campaign could not be loaded" message={errorText(campaign.error)} onRetry={() => void campaign.refetch()} />;
  const c = campaign.data;
  const launched = c.published !== null || c.firstPublishedAt !== null;
  const tab: AnalyticsTab = search.tab ?? 'overview';
  const tabs: Array<{ value: AnalyticsTab; content: ReactNode }> = [
    { value: 'overview', content: <Overview environmentId={environmentId} campaign={c} /> },
    { value: 'variants', content: <VariantStatsPanel environmentId={environmentId} object={{ type: 'campaign', id: campaignId }} period={period} /> },
    { value: 'funnel', content: <FunnelReportPanel environmentId={environmentId} source={{ campaignId }} channels={c.channels} period={period} onPeriodChange={(p) => set({ from: p.from, to: p.to })} /> },
    { value: 'retention', content: <RetentionReportPanel environmentId={environmentId} source={{ campaignId }} channels={c.channels} period={period} onPeriodChange={(p) => set({ from: p.from, to: p.to })} /> },
    { value: 'activity', content: <ActivityLog environmentId={environmentId} scope={{ campaignId }} /> },
  ];
  return (
    <section className="campaign-analytics">
      <p>
        <Link to="/env/$environmentId/campaigns/$campaignId" params={{ environmentId, campaignId }}>
          ← {c.name}
        </Link>
      </p>
      <h1>
        Analytics: {c.name} <CampaignStatusBadge campaign={c} />
      </h1>
      <ChangesBadge environmentId={environmentId} objectType="campaign" objectId={campaignId} label={c.name} />
      {!launched ? (
        <EmptyState
          title="No data yet"
          description="Launch the campaign to see analytics."
          action={
            <Link to="/env/$environmentId/campaigns/$campaignId" params={{ environmentId, campaignId }}>
              Open the campaign
            </Link>
          }
        />
      ) : (
        <Tabs label="Campaign analytics" value={tab} onValueChange={(v) => set({ tab: v === 'overview' ? undefined : (v as AnalyticsTab) })} items={tabs.map((t) => ({ value: t.value, label: TAB_LABELS[t.value], content: t.content }))} />
      )}
    </section>
  );
}
